import { Controller } from '@hotwired/stimulus'
import Logger from '../lib/logger'

export default class extends Controller {
  static targets = ['filterSummary', 'filterBadge']
  static values = {
    basePath: String,
    dashboardType: String,
    year: Number
  }

  connect() {
    Logger.debug('Dashboard controller connected')
    this.updateFilterSummary()
    this.setupFilterPersistence()
  }

  // Update the filter summary widget
  updateFilterSummary() {
    const filters = this.getActiveFilters()
    const filterCount = Object.keys(filters).filter(key => filters[key]).length

    if (filterCount > 0) {
      this.filterSummaryTarget.classList.remove('d-none')
      this.filterBadgeTarget.textContent = `${filterCount} active filter${filterCount > 1 ? 's' : ''}`
    } else {
      this.filterSummaryTarget.classList.add('d-none')
    }
  }

  // Get currently active filters
  getActiveFilters() {
    const filters = {
      customer_id: this.element.querySelector('#customer_filter_id')?.value,
      end_customer_id: this.element.querySelector('#end_customer_filter_id')?.value,
      service_type_id: this.element.querySelector('#service_type_filter_id')?.value,
      state_id: this.element.querySelector('#state_filter_id')?.value
    }

    // Remove empty values
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value && value !== '')
    )
  }

  // Save filters to localStorage
  setupFilterPersistence() {
    // Load saved filters on connect
    const savedFilters = localStorage.getItem('dashboard_filters')
    if (savedFilters) {
      this.applySavedFilters(JSON.parse(savedFilters))
    }

    // Save filters when they change
    const filterInputs = this.element.querySelectorAll('input, select')
    filterInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.saveFilters()
        this.updateFilterSummary()
      })
    })
  }

  // Apply saved filters to the form
  applySavedFilters(filters) {
    Object.entries(filters).forEach(([key, value]) => {
      const input = this.element.querySelector(`#${key}`)
      if (input) {
        input.value = value
      }
    })
  }

  // Save current filters to localStorage
  saveFilters() {
    const filters = this.getActiveFilters()
    localStorage.setItem('dashboard_filters', JSON.stringify(filters))
  }

  // Clear all filters
  clearFilters(event) {
    event.preventDefault()
    const filterInputs = this.element.querySelectorAll('input, select')
    filterInputs.forEach(input => {
      input.value = ''
    })
    localStorage.removeItem('dashboard_filters')
    this.updateFilterSummary()
    this.applyFilters()
  }

  // Apply filters (triggered by button click)
  applyFilters(event) {
    if (event) event.preventDefault()

    const filters = this.getActiveFilters()
    const url = new URL(this.basePathValue, window.location.origin)

    // Add year parameter
    url.searchParams.append('year', this.yearValue)

    // Add other filters
    Object.entries(filters).forEach(([key, value]) => {
      url.searchParams.append(key, value)
    })

    // Add dashboard type if active
    if (this.dashboardTypeValue === 'active') {
      url.searchParams.append('active', '1')
    }

    window.location.href = url.toString()
  }
}
